import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import TitleHeader from '../common/TitleHeader';
import ImgHeaderCalendar from '@/assets/app/images/common/header/header-calendar.png';
import ImgHeaderStar from '@/assets/app/images/common/header/header-star.png';
import attendIcon from '@/assets/app/images/event/attend.png';
import todayIcon from '@/assets/app/images/event/today.png';
import { vAppAjax } from '@/shared/utils/CommonUtil';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';
import useMissionDialogStore from '@/shared/store/UseMissionDialogStore';
import logger from '@/shared/utils/Logger';
import AppButton from '@/shared/components/ui/new/AppButton';
import useAppStore from '@/shared/store/UseAppStore';

interface MissionReward {
  eventTypeCode: string;
  eventName: string;
  points: number;
}

interface StreakStatus {
  consecutiveDays: number;
  attendedToday: boolean;
  playCompletedToday: boolean;
  dailyClaimed: boolean;
  dailyCanClaim: boolean;
  missionStreakDays: number;
  missionCanClaim: boolean;
  missionRemainingDays: number;
  totalPoints: number;
}

interface StreakClaimResult {
  status: StreakStatus;
  reward: MissionReward;
}

interface StreakCalendar {
  year: number;
  month: number;
  attendedDates: string[]; // yyyy-MM-dd
  today: string; // yyyy-MM-dd
}

interface CalendarCell {
  day: number;
  current: boolean;
}

const WEEK_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const weekdayColor = (idx: number) => {
  if (idx === 0) return '#E1464D';
  if (idx === 6) return '#1AAEFF';
  return '#4B4B4B';
};

const StreakLearning: React.FC = () => {
  const navigate = useNavigate();
  const { setIsShowProfileHeader } = useAppStore();
  const { updateMember } = useAppAuthStore();
  const { openSheet } = useBottomSheetStore();
  const { openMissionDialog } = useMissionDialogStore();

  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth() + 1); // 1~12

  const [status, setStatus] = useState<StreakStatus | null>(null);
  const [calendar, setCalendar] = useState<StreakCalendar | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();
    setIsShowProfileHeader(false);
  }, []);

  useEffect(() => {
    loadCalendar(viewYear, viewMonth);
  }, [viewYear, viewMonth]);

  const loadStatus = async () => {
    await vAppAjax(
      'get',
      'streak/status',
      {},
      (response: AxiosResponse<CommonResult<StreakStatus>>) => {
        const resultData = response.data;
        setLoading(false);
        if (resultData.code === 200) {
          setStatus(resultData.result);
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        setLoading(false);
        logger.error(error);
      },
    );
  };

  const loadCalendar = async (year: number, month: number) => {
    await vAppAjax(
      'get',
      `streak/calendar?year=${year}&month=${month}`,
      {},
      (response: AxiosResponse<CommonResult<StreakCalendar>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setCalendar(resultData.result);
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  const showError = (message?: string) => {
    openSheet('message', {
      type: 'ALERT',
      message: message || '요청 처리 중 오류가 발생했습니다.',
    });
  };

  const showMissionSuccess = (reward: MissionReward) => {
    openMissionDialog({
      message: `<span style="color:#1AAEFF;font-weight:700">${reward.eventName}</span> 미션을 달성했어요`,
      points: reward.points,
    });
  };

  const claimDaily = async () => {
    await vAppAjax(
      'post',
      'streak/daily/claim',
      {},
      (response: AxiosResponse<CommonResult<StreakClaimResult>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setStatus(resultData.result.status);
          updateMember({ totalPoints: resultData.result.status.totalPoints });
          showMissionSuccess(resultData.result.reward);
        } else {
          showError(resultData.message);
        }
      },
      () => showError(),
    );
  };

  const claimMission = async () => {
    await vAppAjax(
      'post',
      'streak/mission/claim',
      {},
      (response: AxiosResponse<CommonResult<StreakClaimResult>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setStatus(resultData.result.status);
          updateMember({ totalPoints: resultData.result.status.totalPoints });
          showMissionSuccess(resultData.result.reward);
          loadCalendar(viewYear, viewMonth);
        } else {
          showError(resultData.message);
        }
      },
      () => showError(),
    );
  };

  const goPrevMonth = () => {
    if (viewMonth === 1) {
      setViewYear(y => y - 1);
      setViewMonth(12);
    } else {
      setViewMonth(m => m - 1);
    }
  };

  const goNextMonth = () => {
    if (viewMonth === 12) {
      setViewYear(y => y + 1);
      setViewMonth(1);
    } else {
      setViewMonth(m => m + 1);
    }
  };

  // 일요일 시작 기준 달력 주 단위 구성
  const calendarWeeks = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth - 1, 1);
    const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
    const prevLastDay = new Date(viewYear, viewMonth - 1, 0).getDate();
    const startWeekday = firstDay.getDay(); // 0(일)~6(토)

    const cells: CalendarCell[] = [];

    for (let i = startWeekday - 1; i >= 0; i -= 1) {
      cells.push({ day: prevLastDay - i, current: false });
    }
    for (let d = 1; d <= daysInMonth; d += 1) {
      cells.push({ day: d, current: true });
    }
    let nextDay = 1;
    while (cells.length % 7 !== 0) {
      cells.push({ day: nextDay, current: false });
      nextDay += 1;
    }

    const weeks: CalendarCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
  }, [viewYear, viewMonth]);

  const attendedDaySet = useMemo(() => {
    if (!calendar) return new Set<number>();
    return new Set(calendar.attendedDates.map(d => Number(d.split('-')[2])));
  }, [calendar]);

  const todayInfo = useMemo(() => {
    if (!calendar) return null;
    const [year, month, day] = calendar.today.split('-').map(Number);
    return { year, month, day };
  }, [calendar]);

  if (loading || !status) {
    return <></>;
  }

  return (
    <div className="flex flex-col h-screen bg-white overflow-y-auto">
      <TitleHeader title="연속 학습" onBack={() => navigate(-1)} />

      <main className="flex-1 px-4 pb-8">
        {/* 상단 연속학습 일차 배지 */}
        <div className="flex justify-center mt-4 mb-6">
          <img src={ImgHeaderCalendar} alt="calendar" className="w-6 h-6" />
          <div
            className="flex items-center gap-2 px-4 py-2 status-item"
            style={{ borderRadius: 0, borderTopRightRadius: 20, borderBottomRightRadius: 20 }}
          >
            <div className="status-text calendar f-one-pop" style={{ padding: 0 }}>
              <span className="status-value" style={{ color: '#E1464D', fontSize: 16 }}>
                {status.consecutiveDays}
              </span>
              <span className="status-unit">일차</span>
            </div>
          </div>
        </div>

        <p className="text-center font-bold mb-4">미션을 달성하면 별 포인트를 받을 수 있어요</p>

        {/* 미션 1: 하루에 한번 출석하고 문제풀기 */}
        <div
          className="flex items-center justify-between rounded-2xl px-4 py-3 mb-3"
          style={{ backgroundColor: '#EAF6FF' }}
        >
          <div>
            <p className="font-bold">하루에 한번</p>
            <p className="text-sm text-gray-500">출석하고 문제풀기</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 font-bold">
              +1 <img src={ImgHeaderStar} className="w-5 h-5" alt="star" />
            </span>
            <AppButton
              disabled={!status.dailyCanClaim}
              onConfirm={claimDaily}
              className="f-one-pop"
              style={{ width: 50, height: 32, fontSize: 11 }}
              backgroundColor={!status.dailyCanClaim ? '#D4D4D4' : undefined}
              boxShadowColor={!status.dailyCanClaim ? '#B7B7B7' : undefined}
              message="받기"
            />
          </div>
        </div>

        {/* 미션 2: 연속학습 5일하고 별포인트 받기 */}
        <div
          className="flex items-center justify-between rounded-2xl px-4 py-3 mb-6"
          style={{ backgroundColor: '#EAF6FF' }}
        >
          <div>
            <p className="font-bold">연속학습 5일하고</p>
            <p className="text-sm text-gray-500">별포인트 받기</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 font-bold">
              +10 <img src={ImgHeaderStar} className="w-5 h-5" alt="star" />
            </span>
            <AppButton
              disabled={!status.missionCanClaim}
              onConfirm={claimMission}
              className="f-one-pop"
              style={{ width: 50, height: 32, fontSize: 11 }}
              backgroundColor={!status.missionCanClaim ? '#D4D4D4' : undefined}
              boxShadowColor={!status.missionCanClaim ? '#B7B7B7' : undefined}
              message="받기"
            />
          </div>
        </div>

        {/* 달력 헤더 (월 이동) */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <AppButton
            onConfirm={goPrevMonth}
            className="f-one-pop"
            style={{ width: 70, height: 32, fontSize: 12 }}
            message="◀&nbsp;&nbsp;지난달"
          />
          <span className="font-bold text-[24px]">
            {viewYear}년 {viewMonth}월
          </span>

          <AppButton
            onConfirm={goNextMonth}
            className="f-one-pop"
            style={{ width: 70, height: 32, fontSize: 12 }}
            backgroundColor={'#FF6969'}
            boxShadowColor={'#EC0000'}
            message="다음달&nbsp;&nbsp;▶"
          />
        </div>

        {/* 달력 */}
        <div className="border rounded-2xl overflow-hidden" style={{ borderColor: '#E5E5E5' }}>
          <div className="grid grid-cols-7 text-center py-2" style={{ backgroundColor: '#F7FBFF' }}>
            {WEEK_LABELS.map((label, idx) => (
              <span key={label} className="font-bold" style={{ color: weekdayColor(idx) }}>
                {label}
              </span>
            ))}
          </div>
          {calendarWeeks.map((week, wi) => (
            <div
              key={wi}
              className="grid grid-cols-7 text-center border-t"
              style={{ borderColor: '#F0F0F0' }}
            >
              {week.map((cell, di) => {
                const isToday =
                  cell.current &&
                  todayInfo?.year === viewYear &&
                  todayInfo?.month === viewMonth &&
                  todayInfo?.day === cell.day;
                const isAttended = cell.current && attendedDaySet.has(cell.day);

                return (
                  <div
                    key={di}
                    className="relative flex items-center justify-center"
                    style={{ opacity: cell.current ? 1 : 0.3, height: 50 }}
                  >
                    <span
                      className="absolute"
                      style={{
                        top: 3,
                        left: 3,
                        color: weekdayColor(di),
                        fontWeight: isToday ? 800 : 400,
                      }}
                    >
                      {cell.day}
                    </span>
                    <div
                      className="w-6 h-6 flex items-center justify-center"
                      style={{ marginTop: 14 }}
                    >
                      {isAttended && <img src={attendIcon} className="w-6 h-6" alt="attend" />}
                      {!isAttended && isToday && (
                        <img src={todayIcon} className="w-6 h-6" alt="today" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* 연속학습 미션 남은 일수 안내 */}
        <p className="text-center font-bold mt-6">
          연속학습 <span style={{ color: '#1AAEFF' }}>{status.missionStreakDays}</span>일차{' '}
          <span style={{ color: '#E1464D' }}>{status.missionRemainingDays}</span>일 남았어요
        </p>
      </main>
    </div>
  );
};

export default StreakLearning;
